import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import type { ExcelUpload_S02_Params } from "@widgets/commonpopup/excel-upload/types";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { CalAm, CalAmBulk, CalAmBulkInit } from "@shared/utils/CommonUtils";
import {
  buildJsAddinfoFromFlags,
  extractAddInfoFlags,
} from "@shared/utils/AddInfoUtils";
import { global } from "@shared/libs/Global";

/**
 * S02 (심플/안정)
 * - S01 validRows를 컬럼정의대로 그대로 주입
 * - effGrouping으로 묶어 전표번호 채번, 전표별 line 1..N
 * - 모든 행 state='insert' 마킹 → getChange()가 insert로 잡힘
 * - 저장: getChange() 그대로 tables.header/detail 키로 전송
 */

type S02WithModalProps = ExcelUpload_S02_Params & {
  modalRef?: React.RefObject<any>;
};

const ExcelUpload_S02_ReviewSave = forwardRef<any, S02WithModalProps>(
  ({ modalRef, ...props }, ref) => {
    const { userInfo } = global();

    const {
      validRows,
      headerColumns,
      detailColumns,
      grouping,
      controller,
      numbering,
      tables,
      fieldMap, // { headerNoField, detailNoField, lineNoField } (필수)
    } = props;

    // === 필수 필드명 ===
    const headerNoField = fieldMap?.headerNoField!;
    const detailNoField = fieldMap?.detailNoField!;
    const lineNoField = fieldMap?.lineNoField!;

    const [popupParent, setPopupParent] = useState<any>(null);

    // === 컬럼 필드 목록 (useMemo로 고정) ===
    const headerFields = useMemo(
      () =>
        (headerColumns ?? [])
          .map((c: any) => String(c?.field ?? ""))
          .filter((f) => f.length > 0),
      [headerColumns]
    );

    const detailFields = useMemo(
      () =>
        (detailColumns ?? [])
          .map((c: any) => String(c?.field ?? ""))
          .filter((f) => f.length > 0),
      [detailColumns]
    );

    // === 유효 그룹핑: 시스템 키 제외 (useMemo 고정) ===
    const effGrouping = useMemo(
      () =>
        (grouping ?? []).filter(
          (f) =>
            !!f &&
            f !== headerNoField &&
            f !== detailNoField &&
            f !== lineNoField
        ),
      [grouping, headerNoField, detailNoField, lineNoField]
    );

    const makeKey = (row: any) =>
      effGrouping.map((f) => row?.[f] ?? "").join("||");

    // numbering 안정화 (의존성 최소화)
    const numberingFn = numbering.fn;
    const { cd_module, no_line, cd_prefix, cd_menu, dateField } =
      numbering.options;

    // === 그리드 refs / state ===
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });

    // === 그룹핑 (useMemo) ===
    const groups = useMemo(() => {
      const map = new Map<string, any[]>();
      if (!validRows || validRows.length === 0) return map;
      for (const r of validRows) {
        const k = makeKey(r);
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(r);
      }
      return map;
    }, [validRows, effGrouping]);

    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);

    // === 데이터 구성: 헤더/디테일 ===
    useEffect(() => {
      let cancelled = false;

      (async () => {
        // 필수 필드 누락 가드
        if (!headerNoField || !detailNoField || !lineNoField) {
          await showDialog(
            "alter",
            "fieldMap(headerNoField/detailNoField/lineNoField)이 누락되었습니다."
          );
          return;
        }

        if (!dateField) {
          await showDialog("alter", "전표채번용 날짜항목이 누락되었습니다.");
          return;
        }

        const H: any[] = [];
        const L: any[] = [];

        for (const [, rows] of Array.from(groups.entries())) {
          const sample = rows[0] ?? {};

          // 전표 채번 (dateField의 "값"을 넣어서 호출)
          const slipNo = await numberingFn(
            cd_module,
            no_line,
            cd_prefix,
            sample?.[dateField],
            cd_menu
          );
          if (cancelled) return;

          // 헤더 1행: headerColumns.field 기준으로 "있는 그대로" 채우고 slipNo 주입
          const hdr = { ...sample, [headerNoField]: slipNo };
          H.push(hdr);

          // 디테일: rows 각각을 detailColumns.field 기준으로 채우고 slipNo/line 부여
          let seq = 0;
          for (const r of rows) {
            seq += 1;
            const d: any = {
              ...r,
              [detailNoField]: slipNo,
              [lineNoField]: seq,
            };

            L.push(d);
          }
        }

        if (cancelled) return;

        // 그리드 비우고
        await gridRefs.current["grid1"].current?.deleteAllRows?.();
        await gridRefs.current["grid2"].current?.deleteAllRows?.();

        // 헤더 추가 (=> modifiedRows.added에 자동 기록)
        for (const hdr of H) {
          const details = L.filter(
            (d) => d[detailNoField] === hdr[headerNoField]
          );

          const calculated = await CalAmBulkInit(hdr, details);
          calculated.forEach((row: any) => {
            const target = L.find((x) => x.KeyField === row.KeyField);
            if (target) {
              Object.assign(target, row);
            }
          });

          // header 합계 계산
          hdr.am = calculated.reduce(
            (sum: any, r: any) => sum + (r.am || 0),
            0
          );
          hdr.am_k = calculated.reduce(
            (sum: any, r: any) => sum + (r.am_k || 0),
            0
          );
          hdr.am_vat = calculated.reduce(
            (sum: any, r: any) => sum + (r.am_vat || 0),
            0
          );
          hdr.am_supply = calculated.reduce(
            (sum: any, r: any) => sum + (r.am_supply || 0),
            0
          );

          gridRefs.current["grid1"].current?.addRow(hdr);
        }

        // 디테일 추가 (=> modifiedRows.added에 자동 기록)
        for (const row of L) {
          gridRefs.current["grid2"].current?.addRow(row);
        }

        // 첫 전표 기준으로 디테일 필터/포커스
        if (H.length > 0) {
          const slip = H[0][headerNoField];
          gridRefs.current["grid1"].current?.selectAndFocusRow(0);
          gridRefs.current["grid2"].current?.applyFilters({
            [detailNoField]: slip,
          });
          gridRefs.current["grid2"].current?.selectAndFocusRow(0);
        }

        // 렌더 후 포커스/필터 + 모든 행 state='insert' 마킹
        setTimeout(() => {
          if (H.length > 0) {
            gridRefs.current["grid1"].current?.selectAndFocusRow(0);
            const slip = H[0]?.[headerNoField];
            if (slip) {
              gridRefs.current["grid2"].current?.applyFilters({
                [detailNoField]: slip,
              });
              gridRefs.current["grid2"].current?.selectAndFocusRow(0);
            }
          }
        }, 0);
      })();

      return () => {
        cancelled = true;
      };
    }, [
      groups, // validRows/effGrouping 바뀔 때만 변경
      headerFields, // useMemo로 고정
      detailFields, // useMemo로 고정
      headerNoField,
      detailNoField,
      lineNoField,
      numberingFn, // 함수 레퍼런스
      cd_module,
      no_line,
      cd_prefix,
      cd_menu,
      dateField, // 원시값
    ]);

    const loadAddInfoFlags = async (idTarget = "grid1") => {
      const result: any = await fnGetDataSet({
        id: "sys_addinfo_pop_01_01",
        params: {
          cd_firm: userInfo?.cd_firm ?? "",
          cd_menu: numbering.options.cd_menu ?? "",
          id_target: idTarget,
        },
      });
      const rows = result?.[0] || [];
      const flags = extractAddInfoFlags(rows);
      return flags;
    };

    // === 상단 포커스 → 하단 필터 ===
    const onFocusedRowChanged = (row: any) => {
      if (!row) return;
      const slip = row?.[headerNoField];
      gridRefs.current["grid2"].current?.applyFilters({
        [detailNoField]: slip,
      });
      gridRefs.current["grid2"].current?.selectAndFocusRow(0);
    };

    // === 저장 전 행 정규화 ===
    const ERROR_COL_ID = "__upload_error_summary";

    // 날짜류 컬럼은 구분자 제거(2026-09-03 → 20260903)
    const normalizeDateValue = (value: unknown) => {
      if (typeof value !== "string") return value;
      const trimmed = value.trim();
      if (!trimmed) return value;
      return trimmed.replace(/[^\d]/g, "");
    };

    // 오류내용 표시 컬럼 제거 + cd_firm 보정 + dt_* 정규화
    const normalizeRowForSave = (row: Record<string, any>) => {
      const next = { ...row };
      delete next[ERROR_COL_ID];
      const firm = userInfo?.cd_firm;
      if (
        firm &&
        (next.cd_firm == null || String(next.cd_firm).trim() === "")
      ) {
        next.cd_firm = firm;
      }
      Object.keys(next).forEach((key) => {
        if (key.startsWith("dt_")) {
          next[key] = normalizeDateValue(next[key]);
        }
      });
      return next;
    };

    // === 저장 ===
    const onSave = async (): Promise<boolean> => {
      if (
        !gridRefs.current["grid1"].current ||
        !gridRefs.current["grid2"].current
      )
        return false;
      const soh = gridRefs.current["grid1"].current.getChange();
      const sol = gridRefs.current["grid2"].current.getChange();

      if (
        (soh?.modifiedRowcount ?? 0) === 0 &&
        (sol?.modifiedRowcount ?? 0) === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const addInfoFlags = await loadAddInfoFlags("grid1");
      const addInfoFlags2 = await loadAddInfoFlags("grid2");

      // 정규화 + (부가항목 키가 있으면) js_addinfo 세팅
      const toSaveRows = (rows: any[] | undefined, flags: string[]) =>
        (rows ?? []).map((row: any) => {
          const next = normalizeRowForSave(row);
          if (flags.length > 0) {
            next.js_addinfo = buildJsAddinfoFromFlags(next, flags);
          }
          return next;
        });

      const sohSaveData = {
        ...soh,
        insert: toSaveRows(soh.insert, addInfoFlags),
        update: toSaveRows(soh.update, addInfoFlags),
        delete: (soh.delete ?? []).map(normalizeRowForSave),
      };

      const solSaveData = {
        ...sol,
        insert: toSaveRows(sol.insert, addInfoFlags2),
        update: toSaveRows(sol.update, addInfoFlags2),
        delete: (sol.delete ?? []).map(normalizeRowForSave),
      };

      const payload: Record<string, any> = {
        [String(tables.header)]: sohSaveData,
        [String(tables.detail)]: solSaveData,
      };

      const rtn: boolean = await fnSave(controller, payload);
      if (rtn) {
        gridRefs.current["grid1"].current?.resetUpdate();
        gridRefs.current["grid2"].current?.resetUpdate();
        return true;
      }

      return false;
    };

    const onCellValueChangedH = async (event: CellValueChangedEvent) => {
      if (!event) return;

      const colId = event.colId;
      const rowData = event.rowData;
      const rowNode = event.rowNode;

      await CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        colId
      );
    };

    const onCellValueChangedL = async (event: CellValueChangedEvent) => {
      if (!event) return;

      const colId = event.colId;
      const rowData = event.rowData;
      const rowNode = event.rowNode;
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();

      await CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
    };

    useImperativeHandle(
      ref,
      () => ({
        getSelectedValue: async () => {
          return await onSave();
        },
      }),
      [onSave]
    );

    const grids = useMemo(
      () => ({
        grid1: {
          columns: headerColumns,
          events: {
            onFocusedRowChanged: onFocusedRowChanged,
            onCellValueChanged: onCellValueChangedH,
          },
        },
        grid2: {
          columns: detailColumns,
          events: {
            onCellValueChanged: onCellValueChangedL,
          },
        },
      }),
      [headerColumns, detailColumns]
    );

    return (
      <div style={{ position: "relative" }}>
        {/* 헤더 */}
        <div style={{ height: "32vh", marginTop: 10 }}>
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={headerColumns}
            rowData={[]}
            events={grids.grid1.events}
            etcProps={{
              rowSelection: "single",
              popupParent: popupParent,
            }}
          />
        </div>

        {/* 디테일 */}
        <div style={{ height: "42vh", marginTop: 10 }}>
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={detailColumns}
            rowData={[]}
            events={grids.grid2.events}
            etcProps={{
              rowSelection: "single",
              popupParent: popupParent,
            }}
          />
        </div>
      </div>
    );
  }
);

export default ExcelUpload_S02_ReviewSave;
